/**
 *  帮我生成一个工具类，工具类需要实现以下功能：
    1.参数datatype支持传入DataType的值，也就是24h、7d、30d、180d的值
    2.自动获取当前时间
    3.然后根据当前时间计算出对应的datatype日期前的时间戳，最后返回计算出的时候和当前时间，命名为startTime和endTime
    根据上面的需求，重新命名文件名称
 *  计算不同时间范围的起始和结束时间戳
 * @file calculateTimeRange.ts
 */

import dayjs from "dayjs";

/**
 * 根据指定的时间范围类型计算开始时间和结束时间的时间戳
 * @param dataType 时间范围类型：24h(24小时)、7d(7天)、30d(30天)、180d(180天)
 * @returns 返回开始时间戳和结束时间戳
 */
export const calculateTimeRange = (dataType?: string) => {
  // 获取当前时间作为结束时间
  const now = dayjs();
  const endTime = now.valueOf();
  let startTime: number;

  // 根据不同的时间范围类型计算开始时间戳
  switch (dataType) {
    case "24h":
      // 当前时间减去24小时
      startTime = now.subtract(24, "hour").valueOf();
      break;
    case "7d":
      // 当前时间减去7天
      startTime = now.subtract(7, "day").valueOf();
      break;
    case "30d":
      // 当前时间减去30天
      startTime = now.subtract(30, "day").valueOf();
      break;
    case "180d":
      // 当前时间减去180天
      startTime = now.subtract(180, "day").valueOf();
      break;
    case "1y":
      // 当前时间减去1年
      startTime = now.subtract(1, "year").valueOf();
      break;
    default:
      // 默认为24小时
      startTime = now.subtract(24, "hour").valueOf();
  }

  return {
    startTime,
    endTime,
  };
};

/**
 * 获取不同时间范围的起始和结束时间戳（单位：秒）
 * 这是一个便捷方法，与calculateTimeRange类似但返回秒级时间戳
 * @param dataType 时间范围类型：24h(24小时)、7d(7天)、30d(30天)、180d(180天)
 * @returns 返回开始时间戳和结束时间戳（单位：秒）
 */
export const getTimeRangeInSeconds = (dataType: string) => {
  const { startTime, endTime } = calculateTimeRange(dataType);
  return {
    startTime: Math.floor(startTime / 1000),
    endTime: Math.floor(endTime / 1000),
  };
};

/**
 * 获取ISO格式的时间范围字符串
 * @param dataType 时间范围类型：24h(24小时)、7d(7天)、30d(30天)、180d(180天)
 * @returns 返回开始和结束时间的ISO格式字符串
 */
export const getTimeRangeISO = (dataType: string) => {
  const now = dayjs();
  let start: dayjs.Dayjs;

  switch (dataType) {
    case "24h":
      start = now.subtract(24, "hour");
      break;
    case "7d":
      start = now.subtract(7, "day");
      break;
    case "30d":
      start = now.subtract(30, "day");
      break;
    case "180d":
      start = now.subtract(180, "day");
      break;
    default:
      start = now.subtract(24, "hour");
  }

  return {
    startTime: start.toISOString(),
    endTime: now.toISOString(),
  };
};

export default calculateTimeRange;
